import { PRACTITIONER_MODALITY } from "@shared/types/PractitionerModality";

const careCenter = {
  megaMenu: {
    healthAreas: "Health areas",
    supplements: "Supplements",
  },
  sideNavigation: {
    plans: {
      individual: "Individual",
      multiPatient: "Multi-{{patientTerm}}",
      templates: "Templates",
    },
  },
  patientDrawer: {
    patientInfo: "{{patientTerm}} information",
  },
  planDrawer: {
    confirm: "Choose selected",
    header: {
      selectPatient: "Select {{patientTerm}}(s)",
      clerkSelectPatient: "Select {{patientTerm}}",
      buildATemplate: "Build a template",
      planConfirmation: "Plan for",
      templateConfirmation: "Template for",
      createPatient: "Create new {{patientTerm}}",
    },
    patientSelection: {
      backLink: "Back to Select options",
      specificPatientTitle: "Specific {{patientTerm}}(s)",
      clerkSpecificPatientTitle: "Select a {{patientTerm}}",
      description:
        "Select one or more {{patientsTerm}} to build a plan, and the selected {{patientsTerm}} will have access.",
      searchPlaceholder: "Search for {{patientTerm}}(s)",
      clerkSearchPlaceholder: "Search for {{patientTerm}}",
      createPatient: "Create {{patientTerm}}",
      selectedPatients:
        "<0>{{selectedCount}}</0> of <0>{{totalCount}}</0> {{patientTerm}} selected",
      allItemsSelected: "<0>All</0> {{patientsTerm}} selected",
      selectAll: "Select all",
    },
    createPatient: {
      backLink: "Back to Specific {{patientTerm}}(s)",
      clerkBackLink: "Back to Select a {{patientTerm}}",
    },
    planSelection: {
      createPlanFor: "Create plan for",
      updatePlanFor: "Update plan for",
      specificPatients: {
        title: "Specific {{patientTerm}}(s)",
        description:
          "Select one or more {{patientsTerm}} to build a plan, and the selected {{patientsTerm}} will have access.",
      },
      allMyPatients: {
        title: "All my {{patientsTerm}}",
        description: "All your current and future {{patientsTerm}} will have access.",
        newDescription:
          "All your current and future {{patientsTerm}} will have access, including minor {{patientsTerm}} managed by parents.",
      },
      entireDispensary: {
        title: "Entire dispensary",
        description:
          "All current and future {{patientsTerm}} in this dispensary, including {{patientsTerm}} from other practitioners (if any), will have access.",
        newDescription:
          "All current and future {{patientsTerm}} in this dispensary will have access, including minor {{patientsTerm}} managed by parents. If other practitioners use this dispensary, their {{patientsTerm}} will also have access.",
        tooltipForSelected:
          "Your account access is limited to only your {{patientsTerm}}. {{capitalizedPatientsTerm}} within the entire dispensary will have access to this plan until you update the access above.",
        tooltipForNotSelected: "Your account access is limited to only your {{patientsTerm}}.",
      },
      createAsTemplate: {
        title: "Create as template",
        description: "Start a new template to speed up your workflow.",
      },
    },
    templateSelection: {
      allStaffSelect: "All Staff Selected",
      selectedStaff: "<0>{{selectedCount}}</0> of <0>{{totalCount}}</0> staff selected",
      backToPlanSelectOptions: "Back to Select options",
      specificStaff: {
        title: "Specific staff",
        description: "Select one or more staff to access this template.",
      },
      allStaff: {
        title: "All staff",
        description: "Share this template with all practitioners and staff in your dispensary.",
      },
      onlyMe: {
        title: "Only me",
        description: "Only you can access this template.",
      },
    },
    templateSpecificStaffSelection: {
      backToTemplateSelectionOptions: "Back to Select staff options",
    },
    templateConfirmation: {
      onlyMeDescription: "Keep this template private.",
      allStaffDescription: "Share template with all practitioners and staff in your dispensary.",
    },
  },
  modality: {
    [PRACTITIONER_MODALITY.certified_nutritionist]: "Certified Nutritionists",
    [PRACTITIONER_MODALITY.eastern_medicine]: "Eastern Medicine Practitioners",
    [PRACTITIONER_MODALITY.fitness_professional]: "Fitness Professionals",
    [PRACTITIONER_MODALITY.health_coach]: "Health Coaches",
    [PRACTITIONER_MODALITY.healthcare_professional]: "Healthcare Professionals",
    [PRACTITIONER_MODALITY.herbalist]: "Herbalists",
    [PRACTITIONER_MODALITY.holistic_healthcare_provider]: "Holistic Healthcare Providers",
    [PRACTITIONER_MODALITY.homeopath]: "Homeopaths",
    [PRACTITIONER_MODALITY.licensed_acupuncturist]: "Licensed Acupuncturists",
    [PRACTITIONER_MODALITY.licensed_chiropractor]: "Licensed Chiropractors",
    [PRACTITIONER_MODALITY.licensed_counselor]: "Licensed Counselors",
    [PRACTITIONER_MODALITY.licensed_dentist]: "Licensed Dentists",
    [PRACTITIONER_MODALITY.licensed_massage_therapist]: "Licensed Massage Therapists",
    [PRACTITIONER_MODALITY.licensed_medical_doctor]: "Licensed Medical Doctors",
    [PRACTITIONER_MODALITY.licensed_naturopathic_doctor]: "Licensed Naturopathic Doctors",
    [PRACTITIONER_MODALITY.licensed_nurse_practitioner]: "Licensed Nurse Practitioners",
    [PRACTITIONER_MODALITY.licensed_nutritionist]: "Licensed Nutritionists",
    [PRACTITIONER_MODALITY.licensed_optometrist]: "Licensed Optometrists",
    [PRACTITIONER_MODALITY.licensed_osteopathic_physician]: "Licensed Osteopathic Physicians",
    [PRACTITIONER_MODALITY.licensed_physical_occupational_therapist]:
      "Licensed Physical/Occupational Therapists",
    [PRACTITIONER_MODALITY.licensed_physician_assistant]: "Licensed Physician Assistants",
    [PRACTITIONER_MODALITY.licensed_practitioner_of_oriental_medicine]:
      "Licensed Practitioners of Oriental Medicine",
    [PRACTITIONER_MODALITY.licensed_veterinary_practitioner]: "Licensed Veterinary Practitioners",
    [PRACTITIONER_MODALITY.medical_osteopathic_doctor]: "Medical/Osteopathic Doctors",
    [PRACTITIONER_MODALITY.naturopathic_doctor_cnme]: "Naturopathic Doctors (CNME)",
    [PRACTITIONER_MODALITY.nurse]: "Nurses",
    [PRACTITIONER_MODALITY.nutritional_counselor]: "Nutritional Counselors",
    [PRACTITIONER_MODALITY.other_licensed_healthcare_provider]:
      "Other Licensed Healthcare Providers",
    [PRACTITIONER_MODALITY.registered_dietitian]: "Registered Dietitians",
    [PRACTITIONER_MODALITY.registered_midwife]: "Registered Midwives",
    [PRACTITIONER_MODALITY.registered_nurse]: "Registered Nurses",
    [PRACTITIONER_MODALITY.registered_pharmacist]: "Registered Pharmacists",
    [PRACTITIONER_MODALITY.student]: "Students",
    [PRACTITIONER_MODALITY.other]: "Health professionals",
  },
  catalog: {
    search: {
      placeholder: "Search for health conditions, ingredients, and more...",
      placeholderMegaMenu: "Search for products...",
    },
    exploreWithLabs: {
      labs: {
        title: "Labs",
        blurb: "Order blood tests and specialty kits.",
      },
      products: {
        title: "Products",
        blurb: "Choose from over 20,000 products.",
      },
      dailyPacks: {
        title: "Daily packs",
        blurb: "Create pill packs to increase {{patientTerm}} adherence.",
      },
    },
    explore: {
      title: "Explore",
      products: "Over 20,000 products to support your {{patientTerm}}'s journey.",
      dailyPacks: "Fit your {{patientTerm}}'s intake into a single digestible daily pack.",
      preMadePlans: "Pre-made plans",
      templates: "Pre-made plans to simplify your workflow.",
      evidenceBasedGuides: "Evidence-based guides",
      yourFavoriteProducts: "Your favorite products",
      fscommitment: {
        title: "Fullscript's commitment to quality products.",
        blurb: " We ensure quality, every step of the way:",
        sourcing: "Sourcing",
        dispensing: "Dispensing",
        distribution: "Distribution",
      },
      popularWithModality: {
        title: "Popular with {{- modality}}",
        genericTitle: "Popular with other practitioners",
        genericDescription:
          "Other practitioners use Fullscript's ingredient database every day to find quality products for their {{patientsTerm}}.",
        description:
          "{{- modality}} use Fullscript's ingredient database every day to find quality products for their {{patientsTerm}}.",
        secondaryTitle: "Popular searches",
      },
      healthAreas: {
        title: "Health areas",
        description:
          "Explore our curated collection of featured health areas, including conditions, ingredients and products that are evidence-based to support your decision.",
        commonIngredientsUsed: "Common ingredients used for {{conditionName}} support.",
        viewTemplate: "View template",
      },
    },
    landing: {
      heading: {
        prescribedByYou: "Frequently prescribed by you",
        recommendedByModality: "Top recommended by ",
        recentlyRecommendedTo: "Recently recommended to ",
        otherPractitioners: "other practitioners",
      },
    },
    detail: {
      backToDetail: "Back to detail",
      backToOtherOptions: "Back to other options",
      invalidProductError: "The product you're looking for could not be found.",
    },
  },
  resources: {
    searchResources: "Search resources...",
    uploadResource: "Upload resource",
    resourceSaved: "Resource saved.",
    changesSaved: "Changes saved.",
    sort: {
      sortBy: "Sort",
      alphabeticalAZ: "A-Z",
      alphabeticalZA: "Z-A",
      recentlyAdded: "Recently added",
      oldest: "Oldest",
    },
    filter: {
      owner: {
        title: "Owner",
        fullscript: "Fullscript-created",
        myUploads: "My uploads",
        all: "All resources",
      },
    },
    myUploads: {
      title: "My uploads",
      emptyTitle: "No resources uploaded",
      emptyBody:
        "Added your own supporting documents to personalize your {{treatmentPlanTermPlural}}",
      emptyButton: "Upload resource",
      exploreButton: "Explore all my uploads",
      defaultPill: "None",
    },
    fullscriptResources: {
      title: "Fullscript {{patientTerm}} resources",
      description:
        "Evidence-based guides, handouts, and infographics - written and reviewed by our Integrative Medical Advisory Team.",
      exploreButton: "Explore all Fullscript resources",
    },
    resourceDetails: {
      resourceName: "Resource name",
      details: "Details",
      download: "Download",
      viewFullResource: "View full resource",
      addToMyLibrary: "Add to My Library",
      removeFromMyLibrary: "Remove from My Library",
      deleteModal: {
        title: "Delete resource",
        body: "This resource will be deleted from your resource library. You won't be able to add it to any future plans.",
        resourceSuccessfullyDeleted: "Resource successfully deleted.",
      },
      saveChangesModal: {
        title: "Changes are not saved",
        body: "You've made edits to this resource that need to be saved before it can be added to a plan.",
      },
      leavePageModal: {
        title: "Changes will be lost",
        body: "You've made edits to this resource that will be lost when leaving this page.",
        leavePage: "Leave page",
      },
    },
    resourcePreview: {
      error:
        "We could not load the preview. You can still view the content in a new tab by clicking the link below",
    },
    uploadResources: {
      title: "Drag and drop your file",
      or: "or",
      cta: "Choose a file",
      fileType: "File type",
      oneTimeResource: {
        title: "One-time resource",
        content:
          "This upload <1>won't be saved</1> to your resource library. It will only be attached to your current plan.",
      },
      futureResource: {
        title: "Save for future use",
        content:
          "This upload <1>will be saved</1> to your resource library to be used for future plans.",
      },
      deleteModal: {
        title: "Delete resource",
        body: "This resource won't be added to a plan or your resource library. You'll be taken back to the upload screen.",
        button: "Confirm",
      },
    },
    buildPlan: "Build plan",
    save: "Save",
    addResources: "Add resources",
    attachDocumentsGuidesOrHandouts: "Attach documents, guides, or handouts",
    saveAndBuild: "Save and build plan",
    saveAndAddToPlan: "Save and add to plan",
    saveChanges: "Save changes",
    delete: "Delete",
    cancel: "Cancel",
    confirm: "Confirm",
    backToUploadResourceStep: "Back to upload resource step",
  },
  planReview: {
    messages: {
      title: "Messages",
      from: "Plan from ",
    },
    resources: {
      title: "Resources",
    },
    products: {
      title: "Products",
      addProducts: "Add products",
    },
    planOptimization: {
      button: "View plan optimizations ({{count}})",
      drawer: {
        title: "Plan optimization",
        description:
          "Improve {{patientTerm}} adherence through our smart suggestions. We'll propose products with better reorder rates, cost, daily dosages, and long-term savings.",
        betaBadgeTooltip:
          "This feature is currently undergoing beta testing. Based on the products included in the plan, we have identified alternative options.",
        footer:
          "Practitioners have full autonomy and responsibility when making decisions based on the following data-driven suggestions.",
        backToSuggestions: "Back to suggestions",
        acceptSuccess: "{{baseProduct}} swapped for {{alternativeProduct}}",
        suggestionCategories: {
          lower_cost_per_unit: {
            title: "Cost-effective",
            titleWithNumber: "Cost-effective ({{numOfSuggestions}})",
            description:
              "For a more cost-effective option, choose a product with a lower price per bottle.",
            categoryValue: "${{ categoryValue }} / unit",
          },
          lower_cost: {
            title: "Prioritize long-term savings",
            titleWithNumber: "Prioritize long-term savings ({{numOfSuggestions}})",
            description:
              "For ongoing supplementation needs, choose a product with a lower monthly cost.",
            categoryValue: "${{ categoryValue }} / month",
          },
          fewer_pills: {
            title: "Reduce daily dosage",
            titleWithNumber: "Reduce daily dosage ({{numOfSuggestions}})",
            description:
              "Simplify a {{patientTerm}}'s supplement routine by choosing a product with a lower daily dose.",
            categoryValue: "{{ categoryValue }} {{ dosageFormat }} / day",
          },
          higher_repurchase_rate: {
            title: "Higher repurchase rate",
            titleWithNumber: "Higher repurchase rate ({{numOfSuggestions}})",
            description:
              "Enhance plan adherence by choosing a product with a higher repurchase rate.",
            categoryValue: "{{ categoryValue }}% repurchase",
          },
        },
        emptyState: {
          title: "No further suggestions available",
          description: "Products you've recently prescribed that have a suggestion will show here.",
        },
        accept: "Accept",
        compareProducts: "Compare products",
      },
    },
    reviewTemplate: "Review template",
    reviewMultiPatientPlan: "Review multi-{{patientTerm}} plan",
    reviewPlan: "Review plan",
    review: "Review",
    oneTimeAttachament: "One-time attachment",
    editPatientDiscount: "Edit {{patientTerm}} discount",
    notFound: "We could not find that plan.",
  },
  toast: {
    recommendation: "Product added",
    labRecommendation: "Test added",
    attachment: "Resource added",
    template: "Template applied",
    panel: "Panel added",
    journey: "Journey added",
    journeyRemoved: "Journey removed",
    journeyCancelled: "Journey cancelled",
  },
  selectPatient: "Select {{patientTerm}}",
  selectPatients: "Select {{patientTerm}}(s)",
  emptyPatient: {
    title: "No {{patientTerm}} selected",
    description: "Select a {{patientTerm}} to view their information as you build.",
  },
  viewPatientDetails: "View {{patientTerm}} details",
  planToolBar: {
    viewPlan: "View plan",
  },
  breadcrumbs: {
    careCenterHome: "Catalog home",
    resources: "Resources",
    products: "Products",
    templates: "Templates",
    dailyPacks: "Daily packs",
    favorites: "Favorites",
    upload: "Upload",
    backTo: "Back to",
  },
  compareProducts: {
    maxToastReached: "You've selected {{max}}/{{max}} products",
    maxProductsComparedTooltip:
      "You can compare up to {{max}} products at a time. Please remove one before selecting this.",
    featuredProductTooltip:
      "<1>Featured product:</1> this product was selected by our team as a top choice for our customers.",
    productMiniCard: {
      add: "Add {{ productName }}",
      remove: "Remove {{ productName }}",
      suggestionTooltip:
        "This is a suggestion based on your primary product. Click the plus icon to add it to your list of products to compare.",
    },
    removePrimaryProductModal: {
      title: "Removing a primary product",
      description: "If you remove a primary product, you will exit your current compare session.",
      accept: "Yes, exit comparing",
      cancel: "No, cancel",
      removeProduct: "Remove product",
      removePrimaryProduct: "Remove primary product",
      successToast: "Compare session ended",
    },
    emptyCellButton: "Add a product",
    exitCompareSessionModal: {
      title: "Active compare session",
      description:
        "If you want to see similar products, you will replace your current compare session.",
      accept: "Yes, replace my session",
      cancel: "No, cancel",
      successToast: "Similar products generated",
    },
  },
  planSummary: "Plan summary",
  templateSummary: "Template summary",
  invalidPlan: "The selected plan does not exist.",
  campaignBanner: {
    notification: {
      title: "New! Menopause protocols to support your patients",
      description:
        "Browse treatment-ready templates and evidence-based ingredients — all in one place",
      primaryCtaLabel: "View menopause support",
    },
    catalog: {
      title: "Menopause care starts here",
      description: "Discover ingredients and protocols designed to support every symptom",
      primaryCtaLabel: "Explore menopause support",
    },
  },
  fullscriptAssistBanner: {
    title: "Meet Fullscript Assist — your clinical co-pilot",
    description:
      "Catch drug interactions, spot potential nutrient depletions, and (coming soon!) gain actionable insights from lab results — all without leaving your plan-writing workflow.",
    button: "Try it out",
  },
  categorySpotlight: {
    menopause: {
      template: {
        title: "Curated plan for menopause",
        description:
          "Fullscript has a curated plan for menopause with hand-picked products and resources.",
      },
      banner: {
        title: "Menopause",
        description:
          "Menopause is a complex hormonal transition affecting neuroendocrine, cardiovascular, and musculoskeletal systems. Symptoms like hot flashes, mood changes, sleep disturbance, and bone loss often require targeted, evidence-based support. Nutritional strategies, botanicals, and lifestyle interventions can help optimize patient outcomes during this stage.",
      },
      PopularProductsForMenopause: "Popular products for menopause",
      commonIngredientsUsed:
        "Common ingredients used for menopause <learnMoreLink>Learn more</learnMoreLink>",
    },
    common: {
      viewTemplate: "View template",
      viewEvidence: "View evidence",
      learnMore: "Learn more",
      seeAllProducts: "See all products",
      viewProducts: "View products",
      exploreByIngredients: "Explore by ingredients",
      learnMoreButton: "<learnMoreLink>Learn more </learnMoreLink>",
    },
  },
  categoryPages: {
    common: {
      viewTemplate: "View template",
      viewEvidence: "View evidence",
    },
  },
} as const;

export { careCenter };
